import React from 'react';
import PropTypes from 'prop-types';
import {compose} from 'redux';
import {withRouter} from 'react-router';
import {connect} from 'react-redux';
import {Header, Segment} from 'semantic-ui-react';
import {find as _find, sortBy as _sortBy} from 'lodash';

import {gql, graphql, withApollo} from 'react-apollo';
import HeaderRenderer from '@appComponents/HeaderRenderer';
import {refetchQueryByName} from '@utils/apollo';
import {convertToInt} from '@utils/helpers';
import {sortDropdownOptionsAlphanumerically} from '@utils/sorters';
import mapModulesToProps from '@utils/mapModulesToProps';
import {
    DELETE_HEADER,
    DELETE_CONFIRMATION,
    DELETING,
    DELETED,
    NOT_DELETED,
    SAVED,
    NOT_SAVED,
} from '@constants/messages';
import * as propertyLicenceContentGraphQl from '@graphql/propertyLicences/content';
import {PropertyLicenceContents, GetPropertyLicenceContents} from '@graphql/propertyLicenceContent/query';
import {PropertyLicenceContentCreateMutation} from '@graphql/propertyLicenceContent/mutation';
import {
    PROPERTY_LICENCE_TYPE_OTHER,
    PROPERTY_LICENCE_TYPE_STAGE_SPORT,
    PROPERTY_LICENCE_TYPE_SPORTRADAR,
    SPORT_TENNIS,
} from '@constants/variables';
import {contentCategoriesFilteredByLevelAndParents} from '@graphql/content';
import {GetTournamentsFilteredByTournamentCategories} from '@graphql/tournament/query';
import stageCategoriesDataPropTypes from '@modules/propertyLicence/utils/stageCategoriesManager';
import useStageCategories from '@modules/propertyLicence/utils/useStageCategories';

import {exportExcel} from '../utils/export/propertyLicenceContentExcel';
import PropertyLicenceContentTable from '../components/PropertyLicenceContentTable';

export class PropertyLicenceContent extends React.Component {
    static propTypes = {
        client: PropTypes.object,
        match: PropTypes.shape({
            params: PropTypes.shape({
                id: PropTypes.string.isRequired,
            }),
        }),
        MessageBox: PropTypes.object.isRequired,
        Modal: PropTypes.object.isRequired,
        propertyLicenceId: PropTypes.number,
        DataPropertyLicenceContent: PropTypes.object.isRequired,
        PropertyLicenceContentCreateMutation: PropTypes.func,
        PropertyLicenceContentDeleteMutation: PropTypes.func,
        PropertyLicenceContentUpdateMutation: PropTypes.func,
        propertyLicenceFromStore: PropTypes.object,
        stageCategoriesManager: stageCategoriesDataPropTypes,
    };

    constructor(props) {
        super(props);

        this.state = {
            contentTiers: undefined,
            propertyLicenceContents: undefined,
            tournamentCategories: undefined,
            tournaments: undefined,
            contentCategoriesSecondLevel: undefined,
            contentCategoriesThirdLevel: undefined,
            loading: true,
            contentCategoryThirdLevelValue: null,
            tournamentValue: null,
        };
    }

    componentDidMount() {
        if (this.props.propertyLicenceFromStore) {
            this.getMainDataFromGraphQL(this.props);
        }
    }

    componentDidUpdate(prevProps) {
        const {DataPropertyLicenceContent: {propertyLicenceContents}} = this.props;

        if (propertyLicenceContents && propertyLicenceContents !== prevProps.DataPropertyLicenceContent.propertyLicenceContents) {
            this.setState(() => ({propertyLicenceContents: propertyLicenceContents}));
        }

        if (this.props.propertyLicenceFromStore !== prevProps.propertyLicenceFromStore) {
            this.getMainDataFromGraphQL(this.props);
        }
    }

    getMainDataFromGraphQL(props) {
        if (props.propertyLicenceFromStore) {
            const propertyLicenceType = parseInt(props.propertyLicenceFromStore.property_licence_type_id);
            let query = PropertyLicenceContents,
                variables = {
                    sport: [parseInt(props.propertyLicenceFromStore.sport_id)],
                    parent: null,
                };

            if (propertyLicenceType === PROPERTY_LICENCE_TYPE_OTHER) {
                query = DropdownDataTypeOtherQuery;
                variables = {
                    level: 2,
                    parents: [parseInt(props.propertyLicenceFromStore.content_category_id)],
                };
            }

            props.client.query({
                query: query,
                variables: variables,
            }).then((data) => {
                const firstStageCats = data?.data?.stageCategories
                    ? sortDropdownOptionsAlphanumerically(
                        data.data.stageCategories.mapDataForDropdownWithIntVal()
                    )
                    : [];

                this.setState(() => ({
                    loading: false,
                    contentTiers: data.data.contentTiers,
                    tournamentCategories: data.data.tournamentCategories
                        ? sortDropdownOptionsAlphanumerically(
                            data.data.tournamentCategories.mapDataForDropdownWithIntVal()
                        )
                        : [],
                    contentCategoriesSecondLevel: data.data.contentCategories
                        ? sortDropdownOptionsAlphanumerically(
                            data.data.contentCategories.mapDataForDropdownWithIntVal()
                        )
                        : [],
                }), () => {
                    this.props.stageCategoriesManager?.setFirstStageCategories(firstStageCats);
                });
            });
        }
    }


    getTournamentsFromGraphQL(tournamentCategoryId) {
        this.setState(() => {
            return {
                tournamentsLoading: true,
            };
        });

        this.props.client.query({
            query: GetTournamentsFilteredByTournamentCategories,
            variables: {
                tournamentCategories: [convertToInt(tournamentCategoryId)],
            },
        }).then((data) => {
            let tournaments = data.data.tournaments;

            if (parseInt(this.props.propertyLicenceFromStore.sport_id) === SPORT_TENNIS) {
                tournaments = tournaments.filter((item) => (
                    0 === tournaments.filter((tournament) => (
                        null !== tournament.parent && tournament.parent.id === item.id
                    )).length
                ));
            }

            this.setState(() => {
                return {
                    tournamentsLoading: false,
                    tournaments: sortDropdownOptionsAlphanumerically(tournaments),
                };
            });
        });
    }

    getCategoriesThirdLevelFromGraphQL(secondLevelId) {
        this.setState(() => {
            return {
                contentCategoriesThirdLevelLoading: true,
            };
        });

        this.props.client.query({
            query: gql(contentCategoriesFilteredByLevelAndParents),
            variables: {
                level: 3,
                parents: [secondLevelId],
            },
        }).then((data) => {
            const categories = data.data.contentCategories
                ? sortDropdownOptionsAlphanumerically(data.data.contentCategories.mapDataForDropdown())
                : [];

            this.setState(() => {
                return {
                    contentCategoriesThirdLevelLoading: false,
                    contentCategoriesThirdLevel: categories,
                };
            });
        });
    }

    handlePropertyLicenceContentAdd = (data) => {
        this.setState(() => ({loading: true}));

        let tournament = null,
            content_category = null,
            stage_category = null,
            content_tier = null;

        if (data.tournament !== undefined) {
            tournament = parseInt(data.tournament);
        }

        if (data.content_category_3 !== undefined) {
            content_category = parseInt(data.content_category_3);
        }

        if (data.second_stage_category !== undefined) {
            stage_category = parseInt(data.second_stage_category);
        }

        if (data.content_tier !== undefined) {
            content_tier = parseInt(data.content_tier);
        }

        this.props.PropertyLicenceContentCreateMutation({
            variables: {
                property_licence: this.props.propertyLicenceId,
                tournament: tournament,
                content_category: content_category,
                stage_category: stage_category,
                content_tier: content_tier,
            },
        }).then(() => {
            this.props.MessageBox.addMessage(
                'propertyLicenceContentMessage',
                SAVED('property licence content'),
                null,
                'success'
            );
            refetchQueryByName('GetPropertyLicenceContents');

            this.props.stageCategoriesManager.resetToFirstCategory();
            this.setState(() => ({
                contentCategoryThirdLevelValue: null,
                tournamentValue: null,
                loading: false,
            }));
        }).catch((error) => {
            this.props.MessageBox.addMessage(
                'propertyLicenceContentMessage',
                NOT_SAVED('property licence'),
                `${error}`,
                'error'
            );
            this.setState(() => ({loading: false}));
        });
    };

    handlePropertyLicenceContentDelete = (event, data) => {
        const entity = _find(this.state.propertyLicenceContents, {'id': data['data-id']});
        let name, output;

        switch (convertToInt(this.props.propertyLicenceFromStore?.property_licence_type_id)) {
            case PROPERTY_LICENCE_TYPE_OTHER:
                name = 'category';
                output = [
                    entity.content_category?.parent?.name ?? null,
                    entity.content_category?.name ?? null,
                ];
                break;
            case PROPERTY_LICENCE_TYPE_STAGE_SPORT:
                name = 'stage category';
                output = [
                    entity.stage_category?.parent?.name ?? null,
                    entity.stage_category?.name ?? null,
                ];
                break;
            case PROPERTY_LICENCE_TYPE_SPORTRADAR:
                name = 'tournament';
                output = [
                    entity.tournament?.tournament_category.name ?? null,
                    entity.tournament?.name ?? null,
                ];
                break;
            default:
                name = '';
                output = null;
        }

        output = output?.filter((val) => null !== val).join(' > ');
        this.props.stageCategoriesManager.reset();
        this.props.Modal.setModalConfirmation({
            header: <Header icon='trash' content={DELETE_HEADER('property licence content')}/>,
            text: DELETE_CONFIRMATION(name, output) +
                '\nThis will also delete the settings and activation status specifically set for this content in the client packages.',
            onYes: () => (this.deletePropertyLicenceContent(data['data-id'])),
        });
    };

    deletePropertyLicenceContent(propertyLicenceContentId) {
        const {
            Modal: {setModal},
            MessageBox: {addMessage},
        } = this.props;

        this.props.Modal.setModalConfirmation({
            text: DELETING('property licence content'),
        });

        this.props.PropertyLicenceContentDeleteMutation({
            variables: {id: propertyLicenceContentId},
        }).then(() => {
            setModal({isVisible: false});
            addMessage(
                'propertyLicenceContentMessage',
                DELETED('property licence content'),
                null,
                'success'
            );
            refetchQueryByName('GetPropertyLicenceContents');
        }).catch((error) => {
            setModal({isVisible: false});
            addMessage(
                'propertyLicenceContentMessage',
                NOT_DELETED('property licence content'),
                `${error}`,
                'error'
            );
        });
    }


    handlePropertyLicenceContentUpdate = (data) => {
        this.setState(() => ({loading: true}));

        this.props.PropertyLicenceContentUpdateMutation({
            variables: {
                id: data.id,
                content_tier: parseInt(data.content_tier),
            },
        }).then(() => {
            this.setState(() => ({loading: false}));
            this.props.MessageBox.addMessage(
                'propertyLicenceContentMessage',
                SAVED('property licence content'),
                null,
                'success'
            );
            refetchQueryByName('GetPropertyLicenceContents');
        }).catch((error) => {
            this.setState(() => ({loading: false}));
            this.props.MessageBox.addMessage(
                'propertyLicenceContentMessage',
                NOT_SAVED('property licence content'),
                `${error}`,
                'error'
            );
        });
    };

    handleTournamentCategoryChange = (event, data) => {
        if (data.value) {
            this.getTournamentsFromGraphQL(data.value);
            this.setState(() => {
                return {
                    tournamentValue: null,
                };
            });

            if (event) {
                event.stopPropagation();
            }
        }

        this.setState(() => ({
            tournaments: undefined,
            tournamentValue: null,
        }));
    };

    handleTournamentChange = (event, data) => {
        this.setState(() => {
            return {
                tournamentValue: data.value,
            };
        });
    };

    handleContentCategorySecondLevelChange = (event, data) => {
        if (data.value) {
            this.getCategoriesThirdLevelFromGraphQL(data.value);

            if (event) {
                event.stopPropagation();
            }
        }

        this.setState(() => ({
            contentCategoriesThirdLevel: [],
            contentCategoryThirdLevelValue: null,
        }));
    };

    handleContentCategoryThirdLevelChange = (event, data) => {
        this.setState(() => {
            return {
                contentCategoryThirdLevelValue: data.value,
            };
        });
    };

    render() {
        const storePropertyLicence = this.props.propertyLicenceFromStore,
            loading = (this.props.DataPropertyLicenceContent.loading || this.state.loading),
            loadingSegment = (loading || !storePropertyLicence);

        let contents = this.state.propertyLicenceContents;

        if (storePropertyLicence) {
            if (parseInt(storePropertyLicence.property_licence_type_id) === PROPERTY_LICENCE_TYPE_OTHER) {
                contents = _sortBy(contents, ['content_category.parent.name', 'content_category.name']);
            } else if (parseInt(storePropertyLicence.property_licence_type_id) === PROPERTY_LICENCE_TYPE_STAGE_SPORT) {
                contents = _sortBy(contents, ['stage_category.parent.name', 'stage_category.name']);
            } else {
                contents = _sortBy(contents, ['tournament.tournament_category.name', 'tournament.name']);
            }
        }

        return (
            <Segment basic>
                <div>
                    <HeaderRenderer
                        exportExcelParams={(!loadingSegment && exportExcel(contents, this.props.match.params.id, storePropertyLicence.property_licence_type_id))}
                        loading={loading}
                        messagesBoxNames='propertyLicenceContentMessage'
                    />
                    <PropertyLicenceContentTable
                        contentTiers={this.state.contentTiers}
                        loading={loading}
                        onAdd={this.handlePropertyLicenceContentAdd}
                        onContentCategorySecondLevelChange={this.handleContentCategorySecondLevelChange}
                        onContentCategoryThirdLevelChange={this.handleContentCategoryThirdLevelChange}
                        onDelete={this.handlePropertyLicenceContentDelete}
                        onTournamentCategoryChange={this.handleTournamentCategoryChange}
                        onTournamentChange={this.handleTournamentChange}
                        onUpdate={this.handlePropertyLicenceContentUpdate}
                        propertyLicence={storePropertyLicence}
                        propertyLicenceContent={contents}
                        stageCategoriesManager={this.props.stageCategoriesManager}
                        tournamentCategories={this.state.tournamentCategories}
                        tournaments={this.state.tournaments}
                        tournamentsLoading={this.state.tournamentsLoading}
                        contentCategoriesSecondLevel={this.state.contentCategoriesSecondLevel}
                        contentCategoriesThirdLevel={this.state.contentCategoriesThirdLevel}
                        contentCategoriesThirdLevelLoading={this.state.contentCategoriesThirdLevelLoading}
                        contentCategoryThirdLevelValue={this.state.contentCategoryThirdLevelValue}
                        tournamentValue={this.state.tournamentValue}
                    />
                </div>
            </Segment>
        );
    }
}

PropertyLicenceContentWrapper.propTypes = {
    client: PropTypes.object,
    data: PropTypes.object,
    DataPropertyLicenceContent: PropTypes.object,
};

function PropertyLicenceContentWrapper(props) {
    const stageCategoriesManager = useStageCategories({
        client: props.client,
        propertyLicenceContents: props.DataPropertyLicenceContent?.propertyLicenceContents,
    });

    return <PropertyLicenceContent {...props} stageCategoriesManager={stageCategoriesManager}/>;
}

const DropdownDataTypeOtherQuery = gql(propertyLicenceContentGraphQl.propertyLicenceContentTypeOtherDropdownData);
const PropertyLicenceContentDeleteMutation = gql(propertyLicenceContentGraphQl.propertyLicenceContentDeleteMutation);
const PropertyLicenceContentUpdateMutation = gql(propertyLicenceContentGraphQl.propertyLicenceContentUpdateMutation);

const mapStateToProps = (state) => {
    return {
        propertyLicenceFromStore: state.app.entities.propertyLicence,
    };
};

const mapDispatchToProps = mapModulesToProps(['Modal', 'MessageBox', 'Entity']);

const PropertyLicenceContentWithApollo = withApollo(PropertyLicenceContentWrapper);
const PropertyLicenceContentWithStore = connect(mapStateToProps, mapDispatchToProps)(PropertyLicenceContentWithApollo);

export default compose(
    graphql(GetPropertyLicenceContents, {
        options: (props) => {
            return {
                notifyOnNetworkStatusChange: true,
                fetchPolicy: 'network-only',
                variables: {
                    propertyLicenceId: props.propertyLicenceId,
                },
            };
        },
        name: 'DataPropertyLicenceContent',
    }),
    graphql(PropertyLicenceContentCreateMutation, {name: 'PropertyLicenceContentCreateMutation'}),
    graphql(PropertyLicenceContentDeleteMutation, {name: 'PropertyLicenceContentDeleteMutation'}),
    graphql(PropertyLicenceContentUpdateMutation, {name: 'PropertyLicenceContentUpdateMutation'})
)(withRouter(PropertyLicenceContentWithStore));
